import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BubbleTabController } from '../BubbleTabController';
import { StandingsTournametList } from '../StandingsTournametList';
import {
  CategoryStanding,
  IStandingGroup,
} from '../../TournamentScorecardPage.types';
import { ClassFormatEnum, GroupStandingsTieBreakerEnum } from '../../../../types/enums';
import { formatTeamCategoryLabel } from '../../../../utils/tournamentCategoryTemplate';

type CategoryStandingsTabsProps = {
  categoryStandings: CategoryStanding[];
  format: ClassFormatEnum;
  groupStandingsTieBreakers?: GroupStandingsTieBreakerEnum[];
};

export const CategoryStandingsTabs: FC<CategoryStandingsTabsProps> = ({
  categoryStandings,
  format,
  groupStandingsTieBreakers,
}) => {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';

  const tabData = useMemo(
    () =>
      categoryStandings.map((standing) => ({
        ...standing,
        categoryName: formatTeamCategoryLabel(
          t,
          { name: standing.categoryName, categoryTemplate: standing.categoryTemplate },
          standing.categoryName,
        ),
      })),
    [categoryStandings, t],
  );

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    categoryStandings[0]?.tournamentCategoryId ?? null,
  );

  useEffect(() => {
    setSelectedCategoryId(categoryStandings[0]?.tournamentCategoryId ?? null);
  }, [categoryStandings]);

  const selectedCategory =
    tabData.find((standing) => standing.tournamentCategoryId === selectedCategoryId) ?? null;

  const groupTabs = useMemo((): IStandingGroup[] => {
    if (!selectedCategory) return [];
    return selectedCategory.groups.map((g) => ({
      isGroup: true as const,
      name: g.name,
      teams: g.teams,
    }));
  }, [selectedCategory]);

  return (
    <div>
      <div className="mb-2">
        <label className="small text-muted me-2">{t(prefix + 'standingsCategoryLabel')}</label>
        <BubbleTabController
          selected={selectedCategory}
          data={tabData}
          keyToShow="categoryName"
          callback={(standing: CategoryStanding) =>
            setSelectedCategoryId(standing.tournamentCategoryId)
          }
        />
      </div>
      {groupTabs.length > 0 ? (
        <StandingsTournametList
          format={format}
          matches={groupTabs}
          groupStandingsTieBreakers={groupStandingsTieBreakers}
        />
      ) : (
        <p className="text-muted small mb-0">{t(prefix + 'standingsCategoryEmpty')}</p>
      )}
    </div>
  );
};
